import { computed, defineComponent, nextTick, onMounted, reactive, ref, watch } from '@vue/composition-api';
import axios from 'axios';
import { SettingsContext } from '@/utilities/settings-context';

import moment from 'moment';
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker';

import { formatAmount } from '@/functions/format-functions';
import { sumTotal, sumVat, calcVat, sumTotalManual } from '@/functions/calc-functions';
import { alertDialogue, confirmDialogue, generalError, inputDialogue } from '@/functions/system-modal-functions';
import { AdminInvoice, BaseOrder, FilterPostingOrders, InvoiceStatus, IsOrder, IsPostingOrder, IsPostingOrderArray, Order, OrderType, PostingOrder, FilterCandidatePostingOrders, IsCandidateOrPostingOrderArray, IsCandidatePostingOrderArray, IsCandidateOrPostingOrder, IsCandidateOrder } from '@/views/admin-billing/admin-billing-types';
import { groupBy, Dictionary } from 'lodash';
import { getExtension } from '@/functions/utility-functions';
import { i18n } from '@/utilities/vue-i18n';
import { copyText } from '@/utilities/copy';
import Vue from 'vue';
import { billingDateFormat, getOrdersPeriod, getPostingInfo, getSortedOrders, sumOrdersDates } from '@/functions/billing';
import { getDefaultPreferences } from '@/functions/global-functions';
import { ModalIconType } from '../system-modals/system-modals';

export default defineComponent({
    props: {
        invoice: [Number, String]
    },

    components: {
        VueCtkDateTimePicker
    },


    setup(prop, context)
    {

        const settings = getDefaultPreferences();

        const props = reactive({
            invoice: prop.invoice ? `${prop.invoice}` : context.root.$route.params.invoice,
        });

        const model = reactive({

            invoice: {} as AdminInvoice,
            invoiceFile: {} as File,

            invoiceStart: '',
            invoiceEnd: '',
            newOrder: {title: '', total: 0} as Omit<Order, 'id' | 'description' | 'orderType'>,
            creatingOrder: false,
            creatingOrderLoading: false,
            updatingOrder: {} as Record<number, {saving: boolean; updating: boolean; order: (Order |PostingOrder)}>,
        });

        const newOrderInput = ref<HTMLInputElement | null>(null);

        watch(() => model.creatingOrder, (newValue) => {
            if(newValue) {
                nextTick(() => {
                    newOrderInput.value?.focus();
                });
            }
        });

        const copying = ref<Record<number, boolean>>({});

        const invoiceStart = computed(() => model.invoice ? moment(model.invoice.start).format(settings.format) : null);
        const invoiceEnd = computed(() => model.invoice ? moment(model.invoice.dueDate).format(settings.format) : null);

        const groupedOrders = computed<Dictionary<BaseOrder[]>>(() => model.invoice ? groupBy(model.invoice.orders, x => x.orderType + (IsPostingOrder(x) ? x.posting.id : x.id)) : {});

        const isValid = computed(() => invoiceStart && invoiceEnd && model.invoiceFile instanceof File);

        const orders = computed(() =>
        {

            if (model.invoice.paperBilling && !model.invoice.orders.find(row => row.id === 0))
            {

                // TODO: get paperinvoice fee object. get vat with invoice id?
                return axios.get<Order>(SettingsContext.getApiUrl('invoiceFee'), { params: { id: model.invoice.id } })
                    .then(response => model.invoice.orders.push(response.data));
            }

            return model.invoice.orders;
        });

        const setInvoice = (invoice: AdminInvoice) =>
        {

            model.invoice = invoice;

            model.invoiceStart = invoice.start ?? model.invoiceStart;
            model.invoiceEnd = invoice.dueDate ?? model.invoiceEnd;

            if (invoice?.bill)
            {
                model.invoiceFile = new File([invoice.bill.bytes], invoice.bill.name, {
                    type: model.invoice.bill.contentType,
                });
            }
        };

        const minMaxDates = (date: string) => ({
            min: moment(date).format(settings.format),
            max: moment(date).add({ month: 12 }).format(settings.format)
        });

        const downloadInvoiceFile = () =>
        {
            const elem = window.document.createElement('a');

            elem.href = `data:${model.invoice.bill.contentType};base64,${model.invoice.bill.bytes}`;//window.URL.createObjectURL(model.invoiceFile);
            elem.download = model.invoiceFile.name;
            document.body.appendChild(elem);
            elem.click();
            document.body.removeChild(elem);
        };


        const getCompanyInvoice = async (id: string) =>
        {

            const response = await axios.get<AdminInvoice>(SettingsContext.getApiUrl('getInvoice'), { params: { id: parseInt(id) } });

            setInvoice(response.data);
        }

        const cancelPayment = async (orderId: number, cancel = true) => {
            await axios.post<number>(`${SettingsContext.getApiUrl('cancelPayment')}/${orderId}`, {
                cancel,
            });

            const order = model.invoice.orders.find(x => x.id === orderId);

            if (order) {
                order.isPaymentCanceled = cancel;
            }
        };

        const applyDiscount = async (order: BaseOrder) => {
            const inputResponse = await inputDialogue({
                message: i18n.t('dialogue.discount'),
                title: i18n.t('dialogue.discount-title'),
                icon: ModalIconType.question,
                initialInputData: order.discount !== 0 ? order.discount.toString(10) : '-1000',
                validation: (v) => !Number.isNaN(+v),
                placeholder: i18n.t('dialogue.discount-placeholder'),
            });

            if(!inputResponse.confirmed) {
                return;
            }

            const discount = parseFloat(inputResponse.input);

            try {
                await axios.post<number>(`${SettingsContext.getApiUrl('applyDiscount')}/${order.id}`, {
                    discount: discount > 0 ? discount * -1 : discount,
                });

                order.discount = discount > 0 ? discount * -1 : discount;
            }
            catch(e) {
                if(axios.isAxiosError(e)) {
                    alertDialogue(i18n.t('dialogue.error-general-title'), e.response?.data.detail ?? i18n.t('dialogue.error-general'));
                }
                else {
                    generalError(false);
                }
            }
        };

        onMounted(() =>
        {

            if (props.invoice)
            {
                getCompanyInvoice(props.invoice);
            }

        });


        const invoiceUpload = (event: Event) =>
        {

            const acceptedFileFormats = ['txt', 'pdf'];

            const fileList = (event.target as HTMLInputElement).files as FileList;

            if (!acceptedFileFormats.includes(getExtension(fileList[0].name)))
            {

                return alertDialogue(i18n.t('general.invoice') as string, i18n.t('dialogue.warning-file-format', ['txt pdf']) as string);
            }

            model.invoiceFile = fileList[0];
        }

        const invoiceSubmit = async () =>
        {
            if (!model.invoiceFile.name)
            {
                await generalError(false);

                return;
            }

            const formData = new FormData();

            formData.append('id', `${model.invoice.id}`);

            formData.append('file', model.invoiceFile);

            try
            {
                const response = await axios.post<AdminInvoice>(SettingsContext.getApiUrl('uploadInvoice'), formData);

                setInvoice(response.data);

                if (!response.data || !response.data.bill)
                {
                    await generalError(false);
                }
            }
            catch {
                await generalError(false);
            }
        }

        const updateInvoice = async <T extends keyof AdminInvoice, V extends AdminInvoice[T]>(key: T, value: V) =>
        {

            try
            {
                const response = await axios.post<AdminInvoice>(SettingsContext.getApiUrl('updateInvoice'), {
                    id: model.invoice.id,
                    key,
                    [key]: value
                });

                setInvoice(response.data);
            }
            catch {
                await generalError(false);
            }
        };

        const companyCopyText = (orders: BaseOrder[], index: number) =>
        {
            const order = orders[0];
            if (IsPostingOrder(order))
            {
                const posting = order.posting;

                const text = `${getPostingInfo(order, posting)} ${getOrdersPeriod(orders.filter(IsPostingOrder))}`;

                copyText(text, {
                    show: () => Vue.set(copying.value, index, true),
                    hide: () => Vue.set(copying.value, index, false),
                });
            }
            if(IsCandidateOrder(order)) {
              const posting = order.posting;

              const text = `${getPostingInfo(order, posting)} ${getOrdersPeriod(orders.filter(IsCandidateOrder))}`;

              copyText(text, {
                  show: () => Vue.set(copying.value, index, true),
                  hide: () => Vue.set(copying.value, index, false),
              });
            }
            else
            {
                copyText(order.title, {
                    show: () => Vue.set(copying.value, index, true),
                    hide: () => Vue.set(copying.value, index, false),
                });
            }
        };

        const copyTitle = (orders: BaseOrder[]) =>
        {
            const order = orders[0];
            if (IsPostingOrder(order))
            {
                const posting = order.posting;

                const text = `${getPostingInfo(order, posting)}`;

                copyText(text);
            }
            else
            {
                copyText(order.title);
            }
        };

        const copyPeriod = (orders: BaseOrder[]) =>
        {
            const order = orders[0];
            if (IsPostingOrder(order))
            {
                const text = `${getOrdersPeriod(orders.filter(IsPostingOrder))}`;

                copyText(text);
            }
            else if(IsCandidateOrder(order)) {
                const text = `${getOrdersPeriod(orders.filter(IsCandidateOrder))}`;

                copyText(text);
            }
        };

        const saveNewOrder = async () => {
            model.creatingOrderLoading = true;

            try {
                const response = await axios.post<Order>(SettingsContext.getApiUrl('createOrder'), {
                    id: model.invoice.id,
                    title: model.newOrder.title,
                    amount: model.newOrder.total / (1 + model.invoice.vat),
                });

                model.invoice.orders = [...model.invoice.orders, response.data];

                model.creatingOrder = false;

                model.newOrder.title = '';

                model.newOrder.total = 0;
            }
            catch(e) {
                console.error(e);
            }

            model.creatingOrderLoading = false;
        };

        const updateOldOrder = async (id: number) => {
            const orderIndex = model.invoice.orders.findIndex(x => x.id == id);

            if (orderIndex == -1) { 
                return;
            }

            const order = model.invoice.orders[orderIndex];

            if(order === null || order === undefined) {
                return;
            }

            model.updatingOrder = {
                ...model.updatingOrder,
                [order.id]: {
                    saving: true,
                    updating: true,
                    order: model.updatingOrder[order.id]?.order ?? order,
                },
            };

            try {
                if(order.orderType !== OrderType.Other) {
                    throw new Error('Can\'t update this type of order');
                }
    
                const response = await axios.post<Order>(SettingsContext.getApiUrl('createOrder') + `/${order.id}`, {
                    title: order.title,
                    amount: order.total / (1 + model.invoice.vat),
                });

                model.invoice.orders.splice(orderIndex, 1, response.data);
            }
            catch(e) {
                console.error(e);
            }

            model.updatingOrder = {
                ...model.updatingOrder,
                [order.id]: {
                    saving: false,
                    updating: false,
                    order: model.updatingOrder[order.id]?.order ?? order,
                },
            };
        };

        const deleteOldOrder = async (id: number) => {
            if(!await confirmDialogue(i18n.t('dialogue.delete-order-title') as string, i18n.t('dialogue.delete-order-message') as string)) {
                return;
            }

            const orderIndex = model.invoice.orders.findIndex(x => x.id == id);

            if (orderIndex == -1) { 
                return;
            }

            const order = model.invoice.orders[orderIndex];

            if(order === null || order === undefined) {
                return;
            }

            model.updatingOrder = {
                ...model.updatingOrder,
                [order.id]: {
                    saving: true,
                    updating: true,
                    order: model.updatingOrder[order.id]?.order ?? order,
                },
            };

            try {
                if(order.orderType !== OrderType.Other) {
                    throw new Error('Can\'t update this type of order');
                }

                await axios.delete<Order>(SettingsContext.getApiUrl('createOrder') + `/${order.id}`);

                model.invoice.orders.splice(orderIndex, 1);
            }
            catch(e) {
                console.error(e);
            }

            delete model.updatingOrder[order.id];
        };

        const cancelUpdatingOrder = (id: number) => {
            const order = model.invoice.orders.findIndex(x => x.id == id);

            if(order === -1) {
                return;
            }

            model.invoice.orders[order] = model.updatingOrder[id].order;

            model.invoice.orders = [...model.invoice.orders];

            delete model.updatingOrder[id];
        };

        const startUpdate: (order: BaseOrder) => void = (order: BaseOrder) => { 
            if(!IsOrder(order)) {
                throw new Error();
            }

            if(!model.updatingOrder[order.id] || !model.updatingOrder[order.id].updating)
            {
                model.updatingOrder = {...model.updatingOrder, [order.id]: {saving: false, updating: true, order: {...order}}};
                
                order.total = order.total + calcVat(order.total, model.invoice.vat);
            }

            nextTick(() => {
                (context.refs['input' + order.id] as HTMLInputElement[] | null)?.[0].focus();
            });
        };

        const sumTotalManualRev = (orders: BaseOrder[]) => {
            const notUpdating = orders.filter(x => !model.updatingOrder[x.id] || !model.updatingOrder[x.id].updating);

            const updating = orders.filter(x => model.updatingOrder[x.id] && model.updatingOrder[x.id].updating);

            return sumTotalManual(notUpdating) + sumTotalManual(updating.map(x => ({...x, total: x.total / (1 + model.invoice.vat)})));
        };

        return {
            model,

            props,

            settings,

            minMaxDates,

            invoiceUpload,

            groupedOrders,

            getSortedOrders,

            invoiceSubmit,

            downloadInvoiceFile,

            updateInvoice,

            isValid,

            orders,

            sumVat,

            sumTotal,

            sumTotalManualRev,

            calcVat,

            DateFormat: billingDateFormat,

            formatAmount,

            InvoiceStatus,

            sumOrdersDates,

            copyText,

            companyCopyText,

            copyPeriod: copyPeriod,

            copyTitle: copyTitle,

            copying,

            getPostingInfo,

            getOrdersPeriod,

            IsPostingOrder,

            FilterPostingOrders,

            FilterCandidatePostingOrders,

            IsPostingOrderArray,

            IsCandidateOrPostingOrderArray,

            saveNewOrder,

            deleteOldOrder,

            cancelUpdatingOrder,

            updateOldOrder,

            startUpdate,

            newOrderInput,

            cancelPayment,

            applyDiscount,

        }
    }
});
